// The last-writer guard (worker POST /sessions → 409 stale_write) compares
// TIMESTAMPS. Not every write that moves album_sessions.last_saved_at changes
// the album: the editor's own « reached the editor » mark (POST …/flow-step)
// stamps it too, racing the editor's first read.
//
// ⚠️ 2026-10-06, test order #1173: a FRESH open by a customer, first autosave
// → 409 → « Cet album a été modifié ailleurs », autosave stopped — her edits
// silently stopped saving. Nobody else had touched the album.
//
// So on a 409 the editor reads the row again. If the album's CONTENT is what
// it last knew from the server, only the stamp moved: adopt the new stamp and
// save again. Any difference in content is a real conflict — banner, as before.

/** The columns that ARE the album (what an autosave writes). Everything else
 *  (last_saved_at, flow_step, status, hand_edited_*…) is bookkeeping. */
export const ALBUM_CONTENT_KEYS = [
  'pages',
  'cover_id',
  'cover_destination_text',
  'cover_year_text',
  'cover_subtitle_text',
  'cover_customizations',
  'photo_gap_mm',
  'photo_gap_color',
] as const;

/** JSON with object keys sorted, so jsonb's own key order never reads as a change. */
function stable(v: unknown): string {
  if (Array.isArray(v)) return `[${v.map(stable).join(',')}]`;
  if (v && typeof v === 'object') {
    const o = v as Record<string, unknown>;
    return `{${Object.keys(o).filter((k) => o[k] !== undefined).sort()
      .map((k) => `${JSON.stringify(k)}:${stable(o[k])}`).join(',')}}`;
  }
  return JSON.stringify(v ?? null);
}

/** A fingerprint of the album's content as a server row shows it. */
export function albumContentKey(row: Record<string, unknown> | null | undefined): string | null {
  if (!row) return null;
  return stable(Object.fromEntries(ALBUM_CONTENT_KEYS.map((k) => [k, row[k] ?? null])));
}

/** True only when the row read back holds exactly the album we last knew. */
export function onlyStampMoved(
  knownKey: string | null,
  fresh: Record<string, unknown> | null | undefined,
): boolean {
  if (!knownKey || !fresh) return false;
  return albumContentKey(fresh) === knownKey;
}
